//  src/shared/cache/dataCache.js
// Lightweight cache: in-memory + localStorage + optional cross-tab broadcast

const mem = new Map(); // fast per-tab memory cache
const isBrowser = typeof window !== "undefined";

const now = () => Date.now();


function readRawLS(key) {
  if (!isBrowser) return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}


function readLS(key) {
    const obj = readRawLS(key);
    return obj && typeof obj === "object" ? obj : null;
}


function writeLS(key, value) {
  if (!isBrowser) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

/**
 * Return the cached value if it's still fresh (expiresAt in future).
 * If only LS has the value, hydrate mem from LS.
 */
export function getCached(key) {
  const m = mem.get(key);
  if (m && m.expiresAt > now()) return m.value;

  const ls = readLS(key);
  if (ls && ls.expiresAt > now()) {
    mem.set(key, ls);
    return ls.value;
  }
  return null;
}


/**
+ * peekCached: return cached value immediately even if EXPIRED (stale).
+ * Use this to hydrate UI synchronously and then refresh in background.
+ */
export function peekCached(key) {
  const m = mem.get(key);
  if (m) return m.value ?? null;
  const ls = readRawLS(key);
  if (ls && typeof ls === "object") {
    // don’t hydrate mem with expired entry; just return its value to avoid flicker
    return ls.value ?? null;
  }
  return null;
}


/**
 * Store a value with TTL (in ms). Writes to mem + localStorage.
 */
export function setCached(key, value, ttlMs) {
  const entry = { value, cachedAt: now(), expiresAt: now() + ttlMs };
  mem.set(key, entry);
  writeLS(key, entry);
}

/**
 * True when we either have no cache or cache is older than staleMs.
 * Use this to decide when to revalidate in background.
 */
export function isStale(key, staleMs) {
  const entry = mem.get(key) || readLS(key);
  if (!entry) return true;
  return now() - entry.cachedAt > staleMs;
}

/**
 * Optional: cross-tab broadcast to update other tabs immediately.
 */
const bc =
  isBrowser && "BroadcastChannel" in window
    ? new BroadcastChannel("masjid-cache")
    : null;

export function broadcastSet(key) {
  if (bc) bc.postMessage({ type: "cache-set", key });
}

export function listenBroadcast(onKeyUpdated) {
  if (!bc) return () => {};
  const handler = (ev) => {
    const data = ev?.data;
    if (data?.type === "cache-set" && typeof onKeyUpdated === "function") {
      onKeyUpdated(data.key);
    }
  };
  bc.addEventListener("message", handler);
  return () => bc.removeEventListener("message", handler);
}
